import { useEffect, useRef, useState } from 'react'
import type {
  AccionMerma,
  AprobarBody,
  SolicitudCambioListItem,
} from '../services/jefeCocina'
import { ModalShell } from './ui/ModalShell'

interface AprobarF11ModalProps {
  solicitud: SolicitudCambioListItem
  onConfirm: (body: AprobarBody) => Promise<void> | void
  onCancel: () => void
}

const OPCIONES_MERMA: { value: AccionMerma; label: string; descripcion: string }[] = [
  { value: 'NO_APLICA', label: 'NO_APLICA — sin merma', descripcion: 'Aun no se habia preparado nada' },
  { value: 'DESCARTAR', label: 'DESCARTAR', descripcion: 'Comida ya preparada se descarta' },
  { value: 'REUTILIZAR_PACIENTE_X', label: 'REUTILIZAR para otro paciente', descripcion: 'Asignar a otro paciente compatible' },
  { value: 'PERSONAL_COCINA', label: 'PERSONAL_COCINA', descripcion: 'Comida pasa a alimentacion personal' },
]

export default function AprobarF11Modal({
  solicitud,
  onConfirm,
  onCancel,
}: AprobarF11ModalProps) {
  const [accionMerma, setAccionMerma] = useState<AccionMerma>('NO_APLICA')
  const [observacion, setObservacion] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Focus inicial dentro del dialog y escape para cerrar.
    dialogRef.current?.focus()
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel, submitting])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await onConfirm({
        accion_merma: accionMerma,
        observacion_chef: observacion.trim() || null,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al aprobar'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalShell
      title="Aprobar cambio de dieta"
      subtitle={
        <>
          Hab. {solicitud.habitacion} — {solicitud.paciente_nombre}
          <br />
          {solicitud.dieta_original_nombre} → {solicitud.dieta_nueva_nombre}
        </>
      }
      onClose={onCancel}
      closeDisabled={submitting}
      maxWidth={480}
    >
      <div ref={dialogRef} tabIndex={-1}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <fieldset>
            <legend className="text-sm font-semibold mb-2 text-gray-900 dark:text-gray-50">
              Accion sobre la comida preparada
            </legend>
            <div className="space-y-2">
              {OPCIONES_MERMA.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <input
                    type="radio"
                    name="accion_merma"
                    value={opt.value}
                    checked={accionMerma === opt.value}
                    onChange={() => setAccionMerma(opt.value)}
                    className="mt-1"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-900 dark:text-gray-50">
                      {opt.label}
                    </span>
                    <span className="block text-xs text-gray-600 dark:text-gray-400">
                      {opt.descripcion}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <label className="block">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-50">
              Observacion (opcional)
            </span>
            <textarea
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
              maxLength={2000}
              rows={3}
              className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50"
              placeholder="Notas para historial..."
            />
          </label>

          {error && (
            <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="flex-1 border border-gray-300 dark:border-gray-600 py-2 rounded text-sm disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-avante-success text-white py-2 rounded text-sm font-semibold disabled:opacity-50"
            >
              {submitting ? 'Aprobando…' : 'Confirmar aprobacion'}
            </button>
          </div>
        </form>
      </div>
    </ModalShell>
  )
}
